import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/Home.css';
import chanduImage from '../assets/chandu.png';

// MUI Icons
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import ScienceIcon from '@mui/icons-material/Science';
import DownloadIcon from '@mui/icons-material/Download';
import Tooltip from '@mui/material/Tooltip';

const roles = [
  'HLASM (IBM Assembler) specialist',
  'Hybrid systems architect (Mainframe + Distributed)',
  'Mainframe modernization strategist',
  'Platform modernization leader',
  'Real-time payment systems expert (FinTech)',
  'AI/ML enthusiast for enterprise systems'
];

const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isReturningFromAbout = Boolean(location.state?.fromAboutPage);

  const [displayedRoles, setDisplayedRoles] = useState<string[]>(
    isReturningFromAbout ? roles : []
  );
  const [currentRole, setCurrentRole] = useState('');
  const [roleIndex, setRoleIndex] = useState(isReturningFromAbout ? roles.length : 0);
  const [charIndex, setCharIndex] = useState(0);
  const [typingDone, setTypingDone] = useState(isReturningFromAbout);

  useEffect(() => {
    if (isReturningFromAbout) {
      navigate(location.pathname, { replace: true });
      return;
    }

    if (roleIndex >= roles.length) {
      setTypingDone(true);
      return;
    }

    if (charIndex < roles[roleIndex].length) {
      const timeout = setTimeout(() => {
        setCurrentRole((prev) => prev + roles[roleIndex][charIndex]);
        setCharIndex((prev) => prev + 1);
      }, 50);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        setDisplayedRoles((prev) => [...prev, roles[roleIndex]]);
        setCurrentRole('');
        setCharIndex(0);
        setRoleIndex((prev) => prev + 1);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [charIndex, roleIndex, isReturningFromAbout, navigate, location.pathname]);

  return (
    <div className="home-container">
      <img src={chanduImage} alt="Chandra Mouli Yalamanchili - Lead Software Architect"  className="profile-image"/>
      <h1 className="title">Chandramouli Yalamanchili</h1>

      <div className="hero-summary">
        <p className="tagline">
          Architecting mission-critical financial systems by bridging mainframe reliability with modern distributed and AI-driven innovation.
        </p>
      </div>

      <div className="typewriter-container">
        {displayedRoles.map((role, idx) => {
          const isLast = idx === displayedRoles.length - 1 && typingDone;
          return (
            <div className="typewriter-line" key={idx}>
              <span className="terminal-prompt">$</span> {role}
              {isLast && <span className="blinking-cursor">█</span>}
            </div>
          );
        })}

        {!typingDone && (
          <div className="typewriter-line current">
            <span className="terminal-prompt">$</span> {currentRole}
            <span className="blinking-cursor">█</span>
          </div>
        )}
      </div>

      <div className="button-container">
        <button
          className="button"
          onClick={() => navigate('/about', { state: { fromHomePage: true } })}>
          View My Work & Journey →
        </button>
      </div>

      <div className="social-icons">
        <Tooltip title="LinkedIn">
          <a
            aria-label="LinkedIn"
            href="https://www.linkedin.com/in/chandu85/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkedInIcon fontSize="medium" />
          </a>
        </Tooltip>

        <Tooltip title="ResearchGate">
          <a
            aria-label="ResearchGate"
            href="https://www.researchgate.net/profile/Chandra-Mouli-Yalamanchili"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ScienceIcon fontSize="medium" />
          </a>
        </Tooltip>

        <Tooltip title="Facebook">
          <a
            aria-label="Facebook"
            href="https://www.facebook.com/chandu.yalamanchili/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FacebookIcon fontSize="medium" />
          </a>
        </Tooltip>

        <Tooltip title="Download Resume">
          <a aria-label="Download Resume" href="/Chandramouli_Yalamanchili.pdf" download>
            <DownloadIcon fontSize="medium" />
          </a>
        </Tooltip>
      </div>
    </div>
  );
};

export default Home;